
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */
import  themen  from 'theme/styles/styled-colors';
import  * as theme  from 'react-syntax-highlighter/dist/esm/styles/hljs';
import SyntaxHighlighter from 'react-syntax-highlighter';


const layoutProps = {
  
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <h1 {...{
      "id": "authentication-in-apache-atlas"
    }}>{`Authentication in Apache Atlas.`}</h1>
    <h3 {...{
      "id": "authentication"
    }}>{`Authentication`}</h3>
    <p>{`Atlas supports following authentication methods`}</p>
    <ul>
      <li parentName="ul"><strong parentName="li">{`File`}</strong></li>
      <li parentName="ul"><strong parentName="li">{`Kerberos`}</strong></li>
      <li parentName="ul"><strong parentName="li">{`LDAP`}</strong></li>
      <li parentName="ul"><strong parentName="li">{`Keycloak (OpenID Connect / OAUTH2)`}</strong></li>
      <li parentName="ul"><strong parentName="li">{`PAM`}</strong></li>
    </ul>
    <p>{`Following properties should be set true to enable the authentication of that type in `}<inlineCode parentName="p">{`atlas-application.properties`}</inlineCode>{` file.`}</p>
    <SyntaxHighlighter wrapLines={true} language="shell" style={theme.dark} mdxType="SyntaxHighlighter">
      {`atlas.authentication.method.kerberos=true|false
atlas.authentication.method.ldap=true|false
atlas.authentication.method.file=true|false
atlas.authentication.method.keycloak=true|false`}
 </SyntaxHighlighter>
    <p>{`If two or more authentication methods are set to true, then the authentication falls back to the latter method if the earlier one fails.
For example if Kerberos authentication is set to true and ldap authentication is also set to true then, if for a request without kerberos principal and keytab LDAP authentication will be used as a fallback scenario.`}</p>
    <h3 {...{
      "id": "file-method"
    }}>{`FILE method.`}</h3>
    <p>{`File authentication requires users' login details in users credentials file in the format specified below and
the file path should set to property `}<inlineCode parentName="p">{`atlas.authentication.method.file.filename`}</inlineCode>{` in `}<inlineCode parentName="p">{`atlas-application.properties`}</inlineCode>{`.`}</p>
    <SyntaxHighlighter wrapLines={true} language="shell" style={theme.dark} mdxType="SyntaxHighlighter">
      {`atlas.authentication.method.file=true
atlas.authentication.method.file.filename=${'sys:atlas.home'}/conf/users-credentials.properties`}
 </SyntaxHighlighter>
    <p>{`The users credentials file should have below format`}</p>
    <SyntaxHighlighter wrapLines={true} language="shell" style={theme.dark} mdxType="SyntaxHighlighter">
      {`username=group::sha256-password`}
 </SyntaxHighlighter>
    <p>{` For e.g.`}</p>
    <SyntaxHighlighter wrapLines={true} language="shell" style={theme.dark} mdxType="SyntaxHighlighter">
      {`admin=ADMIN::e7cf3ef4f17c3999a94f2c6f612e8a888e5b1026878e4e19398b23bd38ec221a`}
    </SyntaxHighlighter>
    <p>{`Users group can be either `}<strong parentName="p">{`ADMIN`}</strong>{`, `}<strong parentName="p">{`DATA_STEWARD`}</strong>{` OR `}<strong parentName="p">{`DATA_SCIENTIST`}</strong></p>
    <p><em parentName="p">{`Note`}</em>{`:-password is encoded with sha256 encoding method and can be generated using unix tool.`}</p>
    <p>{`For e.g.`}</p>
    <SyntaxHighlighter wrapLines={true} language="shell" style={theme.dark} mdxType="SyntaxHighlighter">
      {`echo -n "Password" | sha256sum
e7cf3ef4f17c3999a94f2c6f612e8a888e5b1026878e4e19398b23bd38ec221a  -`}
    </SyntaxHighlighter>
    <h3 {...{
      "id": "kerberos-method"
    }}>{`Kerberos Method.`}</h3>
    <p>{`To enable the authentication in Kerberos mode in Atlas, set the property `}<inlineCode parentName="p">{`atlas.authentication.method.kerberos`}</inlineCode>{` to true in `}<inlineCode parentName="p">{`atlas-application.properties`}</inlineCode></p>
    <SyntaxHighlighter wrapLines={true} language="shell" style={theme.dark} mdxType="SyntaxHighlighter">
      {`atlas.authentication.method.kerberos = true`}
 </SyntaxHighlighter>
    <p>{`Also following properties should be set.`}</p>
    <SyntaxHighlighter wrapLines={true} language="shell" style={theme.dark} mdxType="SyntaxHighlighter">
      {`atlas.authentication.method.kerberos.principal=<principal>/<fqdn>@EXAMPLE.COM
atlas.authentication.method.kerberos.keytab = /<key tab filepath>.keytab
atlas.authentication.method.kerberos.name.rules = RULE:[2:$1@$0](atlas@EXAMPLE.COM)s/.*/atlas/
atlas.authentication.method.kerberos.token.validity = 3600 [ in Seconds (optional)]`}
    </SyntaxHighlighter>
    <h3 {...{
      "id": "ldap-method"
    }}>{`LDAP Method.`}</h3>
    <p>{`To enable the authentication in LDAP mode in Atlas, set the property `}<inlineCode parentName="p">{`atlas.authentication.method.ldap`}</inlineCode>{` to true and also set Ldap type to property `}<inlineCode parentName="p">{`atlas.authentication.method.ldap.type`}</inlineCode>{` to LDAP or AD in `}<inlineCode parentName="p">{`atlas-application.properties`}</inlineCode>{`.
Use AD if connecting to Active Directory.`}</p>
    <SyntaxHighlighter wrapLines={true} language="shell" style={theme.dark} mdxType="SyntaxHighlighter">
      {`atlas.authentication.method.ldap=true
atlas.authentication.method.ldap.type=ldap|ad`}
 </SyntaxHighlighter>
    <p>{`For LDAP or AD the following configuration needs to be set in atlas application properties.`}</p>
    <h3 {...{
      "id": "active-directory"
    }}>{`Active Directory`}</h3>
    <SyntaxHighlighter wrapLines={true} language="shell" style={theme.dark} mdxType="SyntaxHighlighter">
      {`atlas.authentication.method.ldap.ad.domain= example.com
atlas.authentication.method.ldap.ad.url=ldap://<AD server ip>:389
atlas.authentication.method.ldap.ad.base.dn=DC=example,DC=com
atlas.authentication.method.ldap.ad.bind.dn=CN=Administrator,CN=Users,DC=example,DC=com
atlas.authentication.method.ldap.ad.bind.password=<password>
atlas.authentication.method.ldap.ad.referral=ignore
atlas.authentication.method.ldap.ad.user.searchfilter=(sAMAccountName={0})
atlas.authentication.method.ldap.ad.default.role=ROLE_USER`}
 </SyntaxHighlighter>
    <h3 {...{
      "id": "ldap-directroy"
    }}>{`LDAP Directroy`}</h3>
    <SyntaxHighlighter wrapLines={true} language="shell" style={theme.dark} mdxType="SyntaxHighlighter">
      {`atlas.authentication.method.ldap.url=ldap://<Ldap server ip>:389
atlas.authentication.method.ldap.userDNpattern=uid={0},ou=users,dc=example,dc=com
atlas.authentication.method.ldap.groupSearchBase=dc=example,dc=com
atlas.authentication.method.ldap.groupSearchFilter=(member=cn={0},ou=users,dc=example,dc=com
atlas.authentication.method.ldap.groupRoleAttribute=cn
atlas.authentication.method.ldap.base.dn=dc=example,dc=com
atlas.authentication.method.ldap.bind.dn=cn=Manager,dc=example,dc=com
atlas.authentication.method.ldap.bind.password=<password>
atlas.authentication.method.ldap.referral=ignore
atlas.authentication.method.ldap.user.searchfilter=(uid={0})
atlas.authentication.method.ldap.default.role=ROLE_USER`}
 </SyntaxHighlighter>
    <h3 {...{
      "id": "keycloak-method"
    }}>{`Keycloak Method.`}</h3>
    <p>{`To enable Keycloak authentication mode in Atlas, set the property `}<inlineCode parentName="p">{`atlas.authentication.method.keycloak`}</inlineCode>{` to true and also set the property `}<inlineCode parentName="p">{`atlas.authentication.method.keycloak.file`}</inlineCode>{` to the localtion of your `}<inlineCode parentName="p">{`keycloak.json`}</inlineCode>{` in `}<inlineCode parentName="p">{`atlas-application.properties`}</inlineCode>{`.
Also set `}<inlineCode parentName="p">{`atlas.authentication.method.keycloak.ugi-groups`}</inlineCode>{` to false if you want to pickup groups from Keycloak. By default the groups will be picked up from the `}<em parentName="p">{`roles`}</em>{` defined in Keycloak. In case you want to use the groups
you need to create a mapping in keycloak and define `}<inlineCode parentName="p">{`atlas.authentication.method.keycloak.groups_claim`}</inlineCode>{` equal to the token claim name. Make sure `}<strong parentName="p">{`not`}</strong>{` to use the full group path and add the information to the access token.`}</p>
    <SyntaxHighlighter wrapLines={true} language="shell" style={theme.dark} mdxType="SyntaxHighlighter">
      {`atlas.authentication.method.keycloak=true
atlas.authentication.method.keycloak.file=/opt/atlas/conf/keycloak.json
atlas.authentication.method.keycloak.ugi-groups=false`}
 </SyntaxHighlighter>
    <p>{`Setup you keycloak.json per instructions from Keycloak. Make sure to include `}<inlineCode parentName="p">{`"principal-attribute": "preferred_username"`}</inlineCode>{` to ensure readable user names and `}<inlineCode parentName="p">{`"autodetect-bearer-only": true`}</inlineCode>{`.`}</p>
    <SyntaxHighlighter wrapLines={true} language="shell" style={theme.dark} mdxType="SyntaxHighlighter">
      {`{
  "realm": "auth",
  "auth-server-url": "http://keycloak-server/auth",
  "ssl-required": "external",
  "resource": "atlas",
  "public-client": true,
  "confidential-port": 0,
  "principal-attribute": "preferred_username",
  "autodetect-bearer-only": true
}`}
 </SyntaxHighlighter>
    <h3 {...{
      "id": "pam"
    }}>{`PAM.`}</h3>
    <p>{`The prerequisite for enabling PAM authentication, is to have login service file in `}<em parentName="p">{`/etc/pam.d/`}</em></p>
    <p>{`To enable the PAM authentication mode in Atlas.`}</p>
    <ul>
      <li parentName="ul">{`Set the atlas property `}<inlineCode parentName="li">{`atlas.authentication.method.pam`}</inlineCode>{` to true in `}<inlineCode parentName="li">{`atlas-application.properties`}</inlineCode>{`.`}</li>
    </ul>
    <SyntaxHighlighter wrapLines={true} language="shell" style={theme.dark} mdxType="SyntaxHighlighter">
      {`atlas.authentication.method.pam=true`}
    </SyntaxHighlighter>
    <ul>
      <li parentName="ul">{`Set the property `}<inlineCode parentName="li">{`atlas.authentication.method.pam.service=<login service>`}</inlineCode>{` to use desired PAM login service.
For example, set below property to use `}<inlineCode parentName="li">{`/etc/pam.d/login`}</inlineCode>{`.`}</li>
    </ul>
    <SyntaxHighlighter wrapLines={true} language="shell" style={theme.dark} mdxType="SyntaxHighlighter">
      {`atlas.authentication.method.pam.service=login`}
    </SyntaxHighlighter>
    </MDXLayout>;
}

;
MDXContent.isMDXComponent = true;